import type { SimpleFieldConfig } from "../../UniversalCRMTable/types";
import { parseTechnicalIdentifiers } from "@/lib/companies/technical-identifiers";
import { cleanHardCostLineItems, cleanStaffLineItems } from "./line-items";

/**
 * Per-field-type normalization for the create/edit form cleaner.
 *
 * Split out of `form-data-cleaner.ts` (module-size lane L17). Behaviour is
 * unchanged and pinned by `tests/unit/form-data-cleaner.test.ts` +
 * `tests/unit/form-data-cleaner-branches.test.ts`.
 */

/**
 * What the cleaner should do with one field's key on the outgoing payload.
 *
 * A plain enum of "the cleaned value" is not enough: the plain-`json` branch
 * has to REMOVE the key entirely (an empty string must not reach the API as
 * `""`), and several branches deliberately leave the raw value alone. Modelling
 * all three as one discriminated union keeps that distinction explicit instead
 * of smuggling it through an `undefined` that also means "cleared to
 * undefined".
 */
export type FieldCleanResult =
  | { readonly action: "set"; readonly value: unknown }
  | { readonly action: "delete" }
  | { readonly action: "keep" };

const KEEP: FieldCleanResult = { action: "keep" };
const DELETE: FieldCleanResult = { action: "delete" };
const set = (value: unknown): FieldCleanResult => ({ action: "set", value });

/**
 * Tags: drop empty / whitespace-only entries; a non-array becomes `[]`.
 *
 * The `as string[]` is the field contract, not a workaround: `type: "tags"` is
 * declared `string[]`, and the pre-split code called `.trim()` on every entry.
 * A `typeof`-guard here would silently DROP a non-string entry where the
 * original threw, which is a behaviour change this split must not make.
 */
function cleanTags(value: unknown): string[] {
  if (!value || !Array.isArray(value) || value.length === 0) return [];
  const tags = value as string[];
  return tags.filter((tag) => tag && tag.trim());
}

/**
 * Multi-user: accept either bare id strings or `{ id }` user objects and emit
 * a flat array of non-blank id strings.
 */
function cleanMultiUser(value: unknown): string[] {
  if (!value || !Array.isArray(value) || value.length === 0) return [];
  return value
    .map((item: unknown): string | null => {
      if (typeof item === "string") return item;
      if (typeof item === "object" && item !== null && "id" in item) {
        const { id } = item as { id: unknown };
        return typeof id === "string" && id ? id : null;
      }
      return null;
    })
    .filter((userId): userId is string => Boolean(userId && userId.trim()));
}

/**
 * Always pass numbers to the mutation hook as a JS `number`.
 *
 * The Zod input schemas (e.g. `DealsInsertInputSchema`) expect `z.number()`,
 * and DECIMAL/BigFloat string serialization happens at the GraphQL mutation
 * boundary (see `use-deals-hooks.ts`). Stringifying here causes "Must be a
 * number" validation errors. Anything unparseable becomes `undefined`.
 */
function cleanNumber(value: unknown): number | undefined {
  if (value === "" || value === null || value === undefined) return undefined;
  if (typeof value === "number") return isNaN(value) ? undefined : value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return undefined;
    const numValue = Number(trimmed);
    return isNaN(numValue) ? undefined : numValue;
  }
  const coerced = Number(value);
  return isNaN(coerced) ? undefined : coerced;
}

/**
 * String arrays: trim, drop blanks, dedupe (first occurrence wins).
 *
 * Same reasoning as `cleanTags` for the contract cast — the pre-split code
 * called `.trim()` on every entry unconditionally.
 */
function cleanStringArray(value: unknown): string[] {
  if (!value || !Array.isArray(value) || value.length === 0) return [];
  const items = value as string[];
  return items
    .map((item) => item.trim())
    .filter((item) => item && item.length > 0)
    .filter((item, index, self) => self.indexOf(item) === index);
}

/**
 * Plain (non-line-item) json fields, e.g. companies `social_links`: the text
 * input defaults to `""`, which must not hit the API — remove the key instead.
 * A non-string value is left exactly as the form supplied it.
 */
function cleanPlainJson(value: unknown): FieldCleanResult {
  if (value === "" || value === null || value === undefined) return DELETE;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? DELETE : set(trimmed);
  }
  return KEEP;
}

/**
 * Normalize one raw form value according to its field config.
 *
 * Pure: takes the raw value, returns what to do with the key. Unknown field
 * types fall through to `keep`, which is what the original `default:` branch
 * did.
 */
export function cleanFieldValue(field: SimpleFieldConfig, value: unknown): FieldCleanResult {
  switch (field.type) {
    case "tags":
      return set(cleanTags(value));

    case "multi-user":
      return set(cleanMultiUser(value));

    // Empty strings become null for date/time and optional client selections.
    case "due-date":
    case "date":
    case "datetime":
    case "time":
    case "date-worked":
    case "client-select":
      return set(value || null);

    case "text":
    case "textarea":
      // Non-strings are left untouched, as they were before the split.
      return typeof value === "string" ? set(value.trim() || undefined) : KEEP;

    case "number":
      return set(cleanNumber(value));

    case "boolean":
      return set(Boolean(value));

    case "template-select":
      // Template selections drive form population, not storage — keep only the id.
      if (value && typeof value === "object" && "id" in value && value.id) {
        return set(value.id);
      }
      return set(null);

    case "array":
      // technical_identifiers is an OBJECT array — parse/clean via the shared
      // helper (drops malformed rows), NOT the string dedup path.
      return field.customComponent === "TechnicalIdentifiersEditor"
        ? set(parseTechnicalIdentifiers(value))
        : set(cleanStringArray(value));

    case "json":
      if (field.lineItemsType === "staff") return set(cleanStaffLineItems(value));
      if (field.lineItemsType === "hard-cost") return set(cleanHardCostLineItems(value));
      return cleanPlainJson(value);

    default:
      return KEEP;
  }
}
